<template>
  <div>
    <form
      v-bind:class="{
        'o-form': true,
        'o-form--sidebar-program-search': isOnResultsPage,
      }"
    >
      <fieldset class="o-form__section s-content-block s-content-block--small">
        <h4 v-if="isOnResultsPage">International Baccalaureate Search</h4>

        <div v-if="!isOnResultsPage">
          <!-- <p>The International Baccalaureate (IB) program is an enriched secondary school program that provide students with the opportunity to earn advanced credit towards their undergraduate degrees.  If you have completed IB courses, you may be eligible for advanced credit.</p>
        <p><strong>Note:</strong> All equivalencies listed are for <strong>Higher Level (HL)</strong> courses, unless otherwise stated.</p>
        <p>This data applies to the 2011 academic year onward.</p> -->
          <div v-html="searchHeaderContent"></div>
          <br />
        </div>

        <!-- IB Course -->
        <div class="m-field-group m-field-group--contain">
          <label for="">IB Course</label>
          <multiselect
            v-model="filter.ibCourseId"
            @input="
              (val) => (filter.ibCourseId = val === 'all' ? 'all' : val ? { id: val.id } : null)
            "
            :options="courses"
            :track-by="'id'"
            :custom-label="courseCustomLabel"
            :placeholder="'Select Course'"
            :showLabels="false"
            @select="
              (val) => (filter.ibCourseId = val === 'all' ? 'all' : val ? { id: val.id } : null)
            "
            :disabled="!courses || !courses.length"
            :loading="coursesLoading"
          >
            <template #option="props">
              <span :title="courseCustomLabel(props.option)">
                {{ courseCustomLabel(props.option) }}
              </span>
            </template>
          </multiselect>
        </div>

        <!-- Institution -->
        <div class="m-field-group">
          <label for="">Institution</label>
          <multiselect
            v-model="filter.institutionId"
            @input="
              (val) => (filter.institutionId = val === 'all' ? 'all' : val ? { id: val.id } : null)
            "
            :options="institutions"
            :track-by="'id'"
            :custom-label="institutionCustomLabel"
            :placeholder="'Select Institution'"
            :showLabels="false"
            @select="
              (val) => (filter.institutionId = val === 'all' ? 'all' : val ? { id: val.id } : null)
            "
            :disabled="!institutions || !institutions.length"
            :loading="institutionsLoading"
          >
            <template #option="props">
              <span :title="institutionCustomLabel(props.option)">
                {{ institutionCustomLabel(props.option) }}
              </span>
            </template>
          </multiselect>
        </div>

        <!-- Academic Year -->
        <div class="m-field-group m-field-group--contain">
          <label for="">Academic Year</label>
          <select v-model="filter.year">
            <option :value="null">All Years (2011 onwards)</option>
            <option
              v-for="yearsOption in yearsOptions"
              :key="yearsOption.year"
              :value="yearsOption.year"
            >
              {{ yearsOption.label }}
            </option>
          </select>
        </div>
      </fieldset>
      <button type="button" class="a-button" @click="submitSearch">Search</button>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue'
import { defineProps, defineEmits, defineExpose } from 'vue'
import axios from 'axios'
import Multiselect from 'vue-multiselect'
import _ from 'lodash'

const internationalBaccalaureateSearchFilterDataStorageName =
  'international-baccalaureate-search-params'

const props = defineProps({
  nonce: { type: String, required: true },
  basePagePath: { type: String, default: false },
  resultsPagePath: { type: String, default: false },
  isOnResultsPage: { type: Boolean, default: false },
  searchHeaderContent: { type: String, required: true },
  apiUrls: { type: Object, required: true },
})

const emit = defineEmits(['onSubmit'])

const filterDataLocalStorage = sessionStorage.getItem(
  internationalBaccalaureateSearchFilterDataStorageName,
)

const filter = reactive({
  institutionId: 'all',
  year: _.isString(filterDataLocalStorage)
    ? JSON.parse(filterDataLocalStorage).year
    : new Date().getMonth() >= 5
      ? new Date().getFullYear() % 100
      : (new Date().getFullYear() - 1) % 100,
  ibCourseId: 'all',
})

if (_.isString(filterDataLocalStorage)) {
  const parsedData = JSON.parse(filterDataLocalStorage)
  Object.assign(filter, parsedData)
}

const institutions = ref([])
const yearsOptions = ref([])
const courses = ref([])
const institutionsLoading = ref(false)
const coursesLoading = ref(false)

const getInstitutions = (ibCourseId = null) => {
  institutionsLoading.value = true
  axios
    .get(props.apiUrls.searchBaseURL + 'ib/GetInstitutions', {
      params: { ibCourseId },
    })
    .then((response) => {
      if (!response.data.find((institution) => institution.id === null)) {
        response.data.unshift({
          id: 'all',
          code: 'All',
          title: 'All Institutions',
        })
      }
      institutions.value = _.sortBy(response.data, [
        (institution) => (institution.id === 'all' ? -Infinity : institution.title),
      ])
      institutionsLoading.value = false
    })
    .catch((error) => {
      console.error(error)
      institutionsLoading.value = false
    })
}

const getCourses = (institutionId = null, academicYear = null) => {
  coursesLoading.value = true
  axios
    .get(props.apiUrls.searchBaseURL + 'ib/GetCourses', {
      params: { institutionId, academicYear },
    })
    .then((response) => {
      if (!response.data.find((course) => course.id === null)) {
        response.data.unshift({
          id: 'all',
          number: 'All Courses',
        })
      }
      courses.value = _.sortBy(response.data, [
        (course) => (course.id === 'all' ? -Infinity : course.number),
      ])
      coursesLoading.value = false
    })
    .catch((error) => {
      console.error(error)
      coursesLoading.value = false
    })
}

const getYearsSelectOptionData = () => {
  const startYear = 2011
  const currentYear =
    new Date().getMonth() >= 5 ? new Date().getFullYear() : new Date().getFullYear() - 1
  const years = []

  for (let i = startYear; i <= currentYear; i++) {
    years.push({
      year: i % 100,
      label: `${i}/` + _.toString(i + 1).slice(-2),
    })
  }

  return years.reverse()
}

const updatePersistence = () => {
  sessionStorage.setItem(
    internationalBaccalaureateSearchFilterDataStorageName,
    JSON.stringify(filter),
  )
}

const clearPersistence = () => {
  sessionStorage.removeItem(internationalBaccalaureateSearchFilterDataStorageName)
}

const newSearch = () => {
  clearPersistence()
  window.location.href = props.basePagePath
}

const submitSearch = () => {
  updatePersistence()
  if (!props.isOnResultsPage) {
    window.location = props.resultsPagePath
    return
  }
  submit()
}

const submit = () => {
  emit('onSubmit', generateSubmitData(filter))
}

const generateSubmitData = (filter) => {
  const data = {
    institutionId: filter.institutionId.id,
    year: filter.year,
    ibCourseId: filter.ibCourseId.id,
  }

  return data
}

const courseCustomLabel = (selectedOption) => {
  if (selectedOption === null || selectedOption === undefined) {
    return 'All Courses'
  }

  const course =
    typeof selectedOption === 'object'
      ? courses.value.find((course) => course.id === selectedOption.id)
      : courses.value.find((course) => course.id === selectedOption)

  if (course && course.id !== null) {
    return course.number
  }

  return 'All Courses'
}

const institutionCustomLabel = (selectedOption) => {
  if (selectedOption === null || selectedOption === undefined) {
    return 'All Institutions'
  }

  const institution =
    typeof selectedOption === 'object'
      ? institutions.value.find((institution) => institution.id === selectedOption.id)
      : institutions.value.find((institution) => institution.id === selectedOption)

  if (institution && institution.id !== null) {
    return institution.id === 'all'
      ? institution.title
      : institution.code + ' - ' + institution.title
  }

  return 'All Institutions'
}

watch(filter, updatePersistence, { deep: true })

watch(
  () => filter.institutionId,
  (newInstitutionId) => {
    getCourses(newInstitutionId.id, filter.year)
  },
)

watch(
  () => filter.year,
  (newYear) => {
    getCourses(filter.institutionId.id, newYear)
  },
)

onMounted(() => {
  getInstitutions()
  yearsOptions.value = getYearsSelectOptionData()
  getCourses()

  if (props.isOnResultsPage) {
    submit()
  }
})

defineExpose({
  newSearch,
})
</script>
